/*
* SessionManager.ts
* @author Abhilash Panwar (abpanwar) Hector Hernandez (hectorh)
* @copyright Microsoft 2020
* File containing apis to help manage session.
*/
import { Utils, EventLatency } from '@microsoft/1ds-core-js';
import { _emptyString, _Extensions, _SessionExtKeys, _EISessionKey, _SignalExtKeys, _UserExtKeys, _UserStateValues } from './Constants';
var delimitter = ';';
var extendTime = 1800000; //30 mins
var maxTime = 86400000; //24 hours
var p = parseInt; // for minimization purpose
/**
 * Class to manage session
 */
var SessionManager = /** @class */ (function () {
    /**
     * Creates an instance of the session manager.
     * @param storageManager storage manager to store session details.
     */
    function SessionManager(storageManager) {
        this._storageManager = storageManager;
        this._session = this._parseSessionString(this._storageManager._getProperty(_EISessionKey));
    }
    /**
     * Process the session state based on the recieved event and add the session fields.
     * @param event Event to add session fields to.
     * @param userCookieExists Indicates if the user cookie was created.
     */
    SessionManager.prototype._processEvent = function (event, userCookieCreated, isHit) {
        //Add session properties
        event.data[_Extensions._SignalExt][_SignalExtKeys._Session] = {};
        var eventTime = this._parseISOString(event.time);
        //Extend or start session for view events
        if (isHit) {
            //Add latency to make it high
            event.latency = EventLatency.RealTime;
            this._extendOrStartSession(eventTime, event, userCookieCreated);
            event.data[_Extensions._SignalExt][_SignalExtKeys._User][_UserExtKeys._State] = this._session.n ? _UserStateValues._New : _UserStateValues._Returning;
            event.data[_Extensions._SignalExt][_SignalExtKeys._Session][_SessionExtKeys._Duration] = eventTime - this._session.s;
        }
        event.data[_Extensions._SignalExt][_SignalExtKeys._Session][_SessionExtKeys._Id] = this._isSessionActive(eventTime) ? this._session.i : _emptyString;
    };
    /**
     * Returns true if a session is active. False otherwise.
     * @param time The event time for the signal that is used to extend or start the session.
     * @param reload Set to true, if we should reload from storage before checking. For view events this should be true.
     */
    SessionManager.prototype._isSessionActive = function (time, reload) {
        if (reload === void 0) { reload = false; }
        if (reload) {
            this._session = this._parseSessionString(this._storageManager._getProperty(_EISessionKey));
        }
        return this._session && Utils.isObject(this._session) && time <= this._session.e;
    };
    /**
     * Extends an ongoing session or starts a new one.
     * @param time The event time for the signal that is used to extend or start the session.
     * @param event The event that is being processed.
     * @param userCookieCreated Indicates if the user user cookie is created.
     */
    SessionManager.prototype._extendOrStartSession = function (time, event, userCookieCreated) {
        this._session = this._parseSessionString(this._storageManager._getProperty(_EISessionKey));
        if (!this._session || this._session.e < time) {
            if (!userCookieCreated) {
                event.data[_Extensions._SignalExt][_SignalExtKeys._User][_UserExtKeys._TimeSinceVisit] = this._session ? time - this._session.e : _emptyString;
            }
            //start new session      
            this._session = {
                i: Utils.createGuid(),
                s: time,
                e: time + extendTime,
                m: time + maxTime,
                n: userCookieCreated
            };
        }
        else {
            //extend existing session
            this._session.e = time + extendTime < this._session.m ? time + extendTime : this._session.m;
        }
        this._storageManager._setProperty(_EISessionKey, this._getSessionString(this._session));
    };
    SessionManager.prototype._parseSessionString = function (sessionString) {
        if (!Utils.isString(sessionString)) {
            return null;
        }
        var sessionArr = sessionString.split(delimitter);
        var session = { i: sessionArr[0], s: p(sessionArr[1], 10), e: p(sessionArr[2], 10) };
        session.m = session.s + maxTime;
        session.n = sessionArr.length === 4; // Has new user part of the session.
        return session;
    };
    SessionManager.prototype._getSessionString = function (session) {
        var sessionString = session.i + delimitter + session.s + delimitter + session.e;
        return session.n ? sessionString + delimitter + session.n : sessionString;
    };
    SessionManager.prototype._parseISOString = function (timeString) {
        var b = timeString.split(/\D+/);
        return Date.UTC(p(b[0], 10), p(b[1], 10) - 1, p(b[2], 10), p(b[3], 10), p(b[4], 10), p(b[5], 10), p(b[6], 10));
    };
    return SessionManager;
}());
export default SessionManager;
//# sourceMappingURL=SessionManager.js.map